import type { HTMLAttributes } from 'react';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './SentimentRating.css';

export type SentimentValue = 'negative' | 'neutral' | 'positive';

export interface SentimentRatingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently selected sentiment; `null` when nothing is selected. */
  value: SentimentValue | null;
  /**
   * Called with the clicked sentiment. Clicking the already-selected face
   * clears the rating (`null`) so the field stays optional.
   */
  onChange?: (value: SentimentValue | null) => void;
  disabled?: boolean;
  /** Accessible group label. Defaults to "How is your experience?". */
  'aria-label'?: string;
}

const FACES: ReadonlyArray<{ value: SentimentValue; label: string; outline: string; filled: string }> = [
  {
    value: 'negative',
    label: 'Negative',
    outline: 'illustrations/illustration-feedback-face-sad-outline.svg',
    filled: 'illustrations/illustration-feedback-face-sad-filled.svg',
  },
  {
    value: 'neutral',
    label: 'Neutral',
    outline: 'illustrations/illustration-feedback-face-neutral-outline.svg',
    filled: 'illustrations/illustration-feedback-face-neutral-filled.svg',
  },
  {
    value: 'positive',
    label: 'Positive',
    outline: 'illustrations/illustration-feedback-face-love-outline.svg',
    filled: 'illustrations/illustration-feedback-face-love-filled.svg',
  },
];

/**
 * Three astronaut-helmet sentiment faces (Figma "Share your thoughts"
 * 9617:25041): sad, kiss (neutral) and heart-eyes. Figma ships each face as
 * two fixed-colour drawings — a grey outline for the unselected state and a
 * blue filled version for the selected one — so the button swaps the asset
 * rather than recolouring it. Nothing is preselected and the rating is
 * optional.
 */
export function SentimentRating({
  value,
  onChange,
  disabled = false,
  'aria-label': ariaLabel = 'How is your experience?',
  className = '',
  ...rest
}: SentimentRatingProps) {
  const classes = ['spark-sentiment-rating', disabled ? 'spark-sentiment-rating-disabled' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div role="group" aria-label={ariaLabel} className={classes} {...rest}>
      {FACES.map((face) => {
        const selected = value === face.value;
        return (
          <button
            key={face.value}
            type="button"
            className={[
              'spark-sentiment-rating-option',
              `spark-sentiment-rating-option-${face.value}`,
              selected ? 'spark-sentiment-rating-option-selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={face.label}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange?.(selected ? null : face.value)}
          >
            <img
              className="spark-sentiment-rating-face"
              src={sparkAssetUrl(selected ? face.filled : face.outline)}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
          </button>
        );
      })}
    </div>
  );
}
